import { SvgIcon } from "@tql/ui-components-react/kendo/common";
import { Input } from "@tql/ui-components-react/kendo/inputs";
import {
	Card,
	CardBody,
	CardHeader,
} from "@tql/ui-components-react/kendo/layout";
import { phone } from "@tql/ui-components-react/untitled/icons";
import { useAtomValue } from "jotai";

import { useDeploymentType } from "~/shared/hooks/use-deployment-type.ts";
import type {
	ShipmentAdditionalDetails,
	ShipmentCarrier,
} from "~/shared/schemas/shipment-management-schema";
import { formatDateToNumeric } from "~/shared/utils/date-formatter.ts";

import {
	useAdditionalDetailsDraft,
	useCarrierDraft,
} from "../../hooks/use-shipment-draft.ts";
import { isEditingShipmentAtom } from "../../state/shipment-edit-atoms.ts";
import { CARRIER_DEFAULT } from "../../utils/defaults.ts";
import { LineItemCost } from "../line-item-cost/line-item-cost.tsx";

export const CarrierCard = ({
	details,
	additionalDetails,
}: {
	details?: ShipmentCarrier;
	additionalDetails?: ShipmentAdditionalDetails;
}) => {
	const carrierDetails: ShipmentCarrier = details ?? CARRIER_DEFAULT;
	const { isInternal } = useDeploymentType();
	const isEditing = useAtomValue(isEditingShipmentAtom);
	const { carrier: carrierDraft, updateCarrier } = useCarrierDraft();
	const { additionalDetails: additionalDetailsDraft, updateAdditionalDetails } =
		useAdditionalDetailsDraft();
	// While editing, show the draft values so edits are reflected live; fall back
	// to the passed-in details before a draft has been seeded.
	const current = carrierDraft ?? carrierDetails;
	const agentProValue =
		(isEditing
			? additionalDetailsDraft?.agentPro
			: additionalDetails?.agentPro) ?? carrierDetails.agentPro;
	const total =
		carrierDetails.charges?.reduce((acc, charge) => {
			if (charge.description?.toLowerCase() === "discount") {
				return acc - charge.amount;
			}
			return acc + charge.amount;
		}, 0) ?? 0;
	return (
		<div
			className={"half-size-card"}
			data-testid={"shipment-management-carrier"}
		>
			<Card className={"full-height shipment-info-card"}>
				<CardHeader>
					<h3>Carrier</h3>
				</CardHeader>
				<CardBody>
					<div className={"party-details carrier-party-details"}>
						<div className={"inline-fields"}>
							<div className={"inline-field"}>
								<p className={"section-label"}>Carrier Name</p>
								<p data-testid={"carrier-name"}>{current.carrierName}</p>
							</div>
						</div>
						<div className={"inline-fields"}>
							<div className={"inline-field"}>
								<p className={"section-label"}>PRO Number</p>
								{isEditing ? (
									<Input
										aria-label={"PRO Number"}
										onChange={(event) =>
											updateCarrier({ pro: String(event.value ?? "") })
										}
										value={current.pro ?? ""}
									/>
								) : (
									<p data-testid={"carrier-pro-number"}>
										{carrierDetails.pro ?? "N/A"}
									</p>
								)}
							</div>
							<div className={"inline-field"}>
								<p className={"section-label"}>Agent PRO</p>
								{isEditing ? (
									<Input
										aria-label={"Agent PRO"}
										onChange={(event) => {
											updateAdditionalDetails({
												agentPro: String(event.value ?? ""),
											});
										}}
										value={agentProValue ?? ""}
									/>
								) : (
									<p data-testid={"carrier-agent-pro"}>
										{agentProValue ?? "N/A"}
									</p>
								)}
							</div>
						</div>
						<div className={"inline-fields"}>
							<div className={"inline-field"}>
								<p className={"section-label"}>Carrier Quote ID</p>
								<p data-testid={"carrier-quote-id"}>
									{carrierDetails.carrierQuoteId ?? "N/A"}
								</p>
							</div>
							<div className={"inline-field"}>
								<p className={"section-label"}>Expiration Date</p>
								<p data-testid={"carrier-expiration-date"}>
									{formatDateToNumeric(carrierDetails.expDate) ?? "N/A"}
								</p>
							</div>
						</div>
						<div className={"info-field carrier-contact"}>
							<p className={"section-label"}>Contact</p>
							<a
								className={"phone-link"}
								href={`tel:${carrierDetails.contactInfo?.phone}`}
							>
								<SvgIcon icon={phone} />
								{carrierDetails.contactInfo?.phone ?? "N/A"}
							</a>
						</div>
					</div>
					{isInternal ? (
						<div className={"rate-summary"}>
							<p className={"section-label"}>Carrier Rate</p>
							<div>
								{carrierDetails.charges?.map((charge) => (
									<LineItemCost
										key={`${charge.description}-${charge.amount}`}
										label={charge.description ?? "Misc Charge"}
										value={`$${charge.amount.toFixed(2)}`}
									/>
								))}
							</div>
							<div className={"price-line-item rate-total"}>
								<p>Total</p>
								<p data-testid={"carrier-total"}>{`$${total.toFixed(2)}`}</p>
							</div>
						</div>
					) : null}
				</CardBody>
			</Card>
		</div>
	);
};
